import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useFocusTrap } from '../../ui/components/useFocusTrap';
import dialogStyles from '../../ui/components/Dialog.module.css';
import styles from './Workout.module.css';

/**
 * Abandon-workout confirmation. Mirrors this codebase's per-feature
 * `DeleteConfirmDialog` convention (own copy, not shared — see
 * `history/DeleteConfirmDialog.tsx`'s own comment) because abandoning is the
 * same kind of irreversible, must-confirm action as deleting: the workout is
 * discarded, not saved to history.
 */
export function AbandonConfirmDialog({
  onConfirm,
  onCancel,
}: {
  onConfirm: () => void;
  onCancel: () => void;
}): React.JSX.Element {
  const panelRef = useRef<HTMLDivElement | null>(null);

  useFocusTrap(panelRef);

  useEffect(() => {
    panelRef.current?.focus();
    function onKeyDown(event: KeyboardEvent): void {
      if (event.key === 'Escape') onCancel();
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onCancel]);

  return createPortal(
    <div className={dialogStyles.dialogOverlay} onClick={onCancel}>
      <div
        ref={panelRef}
        className={dialogStyles.dialogPanel}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="abandon-dialog-title"
        aria-describedby="abandon-dialog-body"
        tabIndex={-1}
        onClick={(event) => event.stopPropagation()}
      >
        <h2 id="abandon-dialog-title" className={dialogStyles.dialogTitleCompact}>
          Abandon Workout?
        </h2>
        <p id="abandon-dialog-body" className={styles.stateText}>
          This workout will be discarded and will not be saved to your history. This cannot be undone.
        </p>

        <div className={styles.actions}>
          <button type="button" className={`${styles.actionButton} ${styles.actionDangerSolid}`} onClick={onConfirm}>
            Abandon Workout
          </button>
          <button type="button" className={styles.actionButton} onClick={onCancel} autoFocus>
            Cancel
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
